/**
 * Badge styling helpers for Appeal Type and Appeal Status table columns.
 */

import { TableCellWithBadge } from "../../../../data-model/tableCells";
import { OrderStatus } from "../../../../enums/authStatus";
import { BadgeColor, BadgeTextColor } from "../../../../enums/coloredText";
import { PrimaryGray } from "../../../../enums/colors";
import { AppealStatus, AppealStatusLabels } from "../../../../enums/appealStatus";
import { AppealType, AppealTypeLabels } from "../../../../enums/appealType";

const DEFAULT_BADGE_TEXT = PrimaryGray.GRAY_1;
const DEFAULT_BADGE_BG = PrimaryGray.GRAY_16;

const defaultBadge: TableCellWithBadge = {
  text: "--",
  color: DEFAULT_BADGE_TEXT,
  bgColor: DEFAULT_BADGE_BG,
  hoverText: "--",
};

const appealStatusToOrderStatus: Record<AppealStatus, OrderStatus> = {
  [AppealStatus.DONE]: OrderStatus.Approved,
  [AppealStatus.PENDING]: OrderStatus.Pending,
  [AppealStatus.IN_PROGRESS]: OrderStatus.IN_PROGRESS,
  [AppealStatus.NOT_STARTED]: OrderStatus.NOT_STARTED,
};

/** Maps backend appeal status strings onto dashboard badge colors. */
const backendStatusToOrderStatus: Record<string, OrderStatus> = {
  "not started": OrderStatus.NOT_STARTED,
  "in progress": OrderStatus.IN_PROGRESS,
  submitted: OrderStatus.Submitted,
  done: OrderStatus.Approved,
  "appeal approved": OrderStatus.Approved,
  "appeal denied": OrderStatus.Denied,
};

/**
 * Builds a dashboard-standard badge cell from {@link OrderStatus} colors.
 *
 * @param text - Badge label shown in the table
 * @param orderStatus - Dashboard status used to resolve badge colors
 * @returns Badge cell data for `CustomTable`
 */
const buildDashboardBadge = (
  text: string,
  orderStatus: OrderStatus,
): TableCellWithBadge => ({
  text,
  color: BadgeTextColor[orderStatus] ?? DEFAULT_BADGE_TEXT,
  bgColor: BadgeColor[orderStatus] ?? DEFAULT_BADGE_BG,
  hoverText: text,
});

/**
 * Resolves badge styles for an appeal submission channel value.
 * Appeal type always uses black text on a gray background.
 *
 * @param appealType - Raw appeal type from the API row
 * @returns Badge cell data for the Appeal Type column
 */
export const getAppealTypeBadgeStyles = (
  appealType: string | null | undefined,
): TableCellWithBadge => {
  if (!appealType?.trim()) {
    return defaultBadge;
  }

  const trimmedType = appealType.trim();
  const normalizedValue = trimmedType.toLowerCase().replace(/\s+/g, "_");
  const matchedType = Object.values(AppealType).find(
    (type) => type.toLowerCase() === normalizedValue,
  );

  let label = trimmedType;
  if (matchedType) {
    label = AppealTypeLabels[matchedType];
  } else if (trimmedType === "Call") {
    label = AppealTypeLabels[AppealType.PHONE];
  } else if (trimmedType === "E-Appeal") {
    label = AppealTypeLabels[AppealType.PORTAL];
  } else if (trimmedType === "Manual") {
    label = AppealTypeLabels[AppealType.MAIL];
  } else if (trimmedType === "Fax") {
    label = AppealTypeLabels[AppealType.FAX];
  }

  return {
    text: label,
    color: DEFAULT_BADGE_TEXT,
    bgColor: DEFAULT_BADGE_BG,
    hoverText: label,
  };
};

/**
 * Resolves badge styles for an appeal workflow status value using dashboard colors.
 *
 * @param appealStatus - Raw appeal status from the API row
 * @returns Badge cell data for the Appeal Status column
 */
export const getAppealStatusBadgeStyles = (
  appealStatus: string | null | undefined,
): TableCellWithBadge => {
  if (!appealStatus?.trim()) {
    return defaultBadge;
  }

  const trimmed = appealStatus.trim();
  const normalizedValue = trimmed.toLowerCase().replace(/\s+/g, "_");
  const matchedStatus = Object.values(AppealStatus).find(
    (status) => status.toLowerCase() === normalizedValue,
  );

  if (matchedStatus) {
    return buildDashboardBadge(
      AppealStatusLabels[matchedStatus],
      appealStatusToOrderStatus[matchedStatus],
    );
  }

  const backendKey = trimmed.toLowerCase().replace(/_/g, " ");
  const orderStatus =
    backendStatusToOrderStatus[backendKey] ?? OrderStatus.NO_STATUS;

  return buildDashboardBadge(trimmed, orderStatus);
};
